/**
 * @fileoverview
 *
 * Functions to initiate and control `_fwn.players`
 * SDK attribute which provides programmatic access
 * to all available players on a scene. Players are
 * accessible by a `widget_id` and `name` key.
 */

import { customEvent } from '@core/customEvent/customEvent'
import { EventTypeReady } from '@core/customEvent/eventTypeReady'
import { getFWN } from '@core/fwn'

import { PlayerControl } from './PlayerControl'
import type { IPlayerInstance, IPlayers } from './types'

const allPlayersCache: IPlayers = {}

/**
 * Getter to access current module level cache
 */
export function fwnPlayersGetter() {
  return allPlayersCache
}

/**
 * Register a player instance under certain keys (widget_id, name).
 *
 * If a PlayerControl is already registered for `widgetId` (which
 * happens when the React effect re-runs after `videoWrapper` becomes
 * available), the existing instance is updated in place so that any
 * state held on the public `_fwn.player` (e.g. a `muted` write issued
 * before the wrapper attached) is preserved across the handoff.
 */
export function registerFWNPlayer(
  widgetId: string,
  name: string,
  instance: IPlayerInstance,
) {
  const fwn = getFWN()
  if (fwn) {
    const existing = fwn.players[widgetId]
    if (existing instanceof PlayerControl) {
      existing.updateInstance(instance)
      return existing
    }

    const player = new PlayerControl(instance)

    fwn.players[widgetId] = player
    if (name) {
      fwn.players[name] = player
    }

    customEvent({
      type: EventTypeReady.FW_PLAYER_READY,
      data: { widgetId, name, player },
    })

    return player
  }
}

/**
 * Unregister - destroy a player instance.
 */
export function unregisterFWNPlayer(widgetId: string, name: string) {
  const fwn = getFWN()
  if (fwn) {
    delete fwn.players[widgetId]
    if (name) {
      delete fwn.players[name]
    }
  }
}
